import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, FlatList } from "react-native";
import { useIsFocused, useRoute } from "@react-navigation/native";
import Text from "theme/Text";
import { useSelector } from "react-redux";
import i18n from "utils/i18n";
import Translate from "utils/translate";
import _ from "lodash";
import FetchManagementBookings from "utils/fetch/management/FetchManagementBookings";
import { bookingStatus, bookingStatusActiveCount } from "utils/constants/booking-constants";
import { textStyles } from "styles/text";
import LoadingComponent from "components/atoms/Loading";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import ThemedBlock from "components/atoms/ThemedBlock";
import ManagementDateSelector from "components/molecules/ManagementDateSelector";
import ThemedToggle from 'components/atoms/ThemedToggle'
import HelperString from 'utils/helper-string'
import { Column, Container, Grid, Row } from 'components/atoms/BootstrapGrid'
import BookingsGroup from 'scenes/management/bookings/_components/BookingsGroup'
import { createBookingList } from 'scenes/management/bookings/_functions/bookings'
import FetchCachedEmployee from 'utils/fetch/cached/FetchCachedEmployee'
import { managementConstants } from 'services/redux/management/constants'
import { fetchManagementBuildingTimeslots } from 'scenes/management/bookings/_functions/timeslots'
import { StatisticItem } from 'scenes/management/bookings/_components/StatisticItem'
import EmployeeSearch from 'scenes/management/bookings/_components/EmployeeSearch'
import { dataConstants } from 'services/redux/data/constants'
import HelperDate, { dateFormat } from 'utils/helper-date'
import { selectManagementSelectedStartDate } from 'services/redux/management/reducers'
import { ThemedValues } from 'styles/themed'

const SHOW = {
	ALL: 'all',
	INVITATIONS: 'invitations',
	SCANNED_IN: 'scanned_in'
}

const ManagementBookingsViewScene = () => {
	
	const isFocused = useIsFocused();
	const route = useRoute();
	
	const buildingGuid = _.get( route.params, 'buildingGuid', null );
	const floorGuid = _.get( route.params, 'floorGuid', null );
	const roomGuid = _.get( route.params, 'roomGuid', null );
	
	const _selectedStartDate = useSelector( selectManagementSelectedStartDate )
	const _managementBuilding = useSelector( state => {
		return state.management[managementConstants.BUILDINGS][buildingGuid];
	} )
	const _managementFloor = useSelector( state => {
		return state.management[managementConstants.FLOORS][floorGuid];
	} )
	const _managementRoom = useSelector( state => {
		return state.management[managementConstants.ROOMS][roomGuid];
	} )
	const _timeslotKinds = useSelector( state => {
		return state.data[dataConstants.TIMESLOT_KINDS];
	} );
	
	const getDate = () => {
		return _selectedStartDate ?? HelperDate.date().format( dateFormat.DATE );
	}
	
	const searchResultEmployees = useRef( null )
	
	const [ loading, setLoading ] = useState( false )
	const [ employees, setEmployees ] = useState( {} )
	const [ employeeBookings, setEmployeesBookings ] = useState( {} )
	const [ employeeBookingsCount, setEmployeesBookingsCount ] = useState( 0 )
	const [ timeslots, setTimeslots ] = useState( [] )
	
	const show = useRef( [] )
	
	const [ uniqueKey, setUniqueKey ] = useState( HelperString.random() )
	
	const showEroOnly = _.get( route.params, 'eroOnly', 'no' ) === 'yes';
	const kind = _.get( route.params, 'kind', null );
	const subKind = _.get( route.params, 'subKind', null );
	const timeslotKind = _.find( _timeslotKinds, { kind: kind, sub_kind: subKind } )
	
	const dateKey = uniqueKey + '-'+ getDate();
	
	const createBookingListItems = () => {
		if( searchResultEmployees.current !== null ) {
			return searchResultEmployees.current;
		}
		
		return employeeBookings;
	}
	
	const createBookingListProps = () => {
		return {
			hideNotScannedIn: _.includes( show.current, SHOW.SCANNED_IN ),
			showInvitationsOnly: show.current.includes( SHOW.INVITATIONS ),
			showEroOnly: showEroOnly
		}
	}
	
	const updateShow = ( name ) => {
		setUniqueKey( HelperString.random() )
		
		if( show.current.includes( name ) ) {
			show.current = _.filter( show.current, e => {
				return e !== name;
			})
		} else {
			show.current.push( name )
		}
	}
	
	const onChangeEmployeeName = ( values ) => {
		searchResultEmployees.current = values === null ? null : _.filter( employeeBookings, o => {
			return _.some( values, ( el ) => {
				return _.includes(o.employee.name, el.name);
			} )
		})
		
		const { list } = createBookingList( createBookingListItems(), createBookingListProps() );
		setTimeslots( list )
	}
	
	const toggleHideNotScannedIn = () => {
		updateShow( SHOW.SCANNED_IN )
		
		const { list } = createBookingList( createBookingListItems(), createBookingListProps() );
		setTimeslots( list )
	}
	
	const toggleShowInvitationsOnly = () => {
		updateShow( SHOW.INVITATIONS )
		
		const { list } = createBookingList( createBookingListItems(), createBookingListProps() );
		setTimeslots( list )
	}
	
	const fetchBookings = ( { withLoading = true, callback = null } = {} ) => {
		FetchManagementBookings.fetch({
			building: {
				guid: buildingGuid
			},
			floor: {
				guid: floorGuid
			},
			room: {
				guid: roomGuid
			},
			date: getDate(),
			kind: kind,
			subKind: subKind,
			status: [
				bookingStatus.ACTIVE,
				bookingStatus.INVITE_ACCEPTED,
				bookingStatus.INVITED,
				bookingStatus.EXPIRED
			],
			callbackStart: () => {
				if( withLoading ) {
					setLoading( true )
				}
			},
			callbackSuccess: ( employeeBookings ) => {
				// Get the timeslots
				const { employees, list, bookings } = createBookingList( employeeBookings, createBookingListProps() );
				
				const employeeBookingsCount = _.size(
					_.filter( bookings, o => {
						return bookingStatusActiveCount.includes( o.status )
					})
				);
				
				setLoading( false )
				setEmployeesBookings( employeeBookings )
				setEmployeesBookingsCount( employeeBookingsCount )
				setTimeslots( list )
				setEmployees( employees )
				
				if( _.isFunction( callback ) ) {
					callback();
				}
			},
			callbackError: () => {
				setLoading( false )
				
				if( _.isFunction( callback ) ) {
					callback();
				}
			}
		});
	}
	
	const fetchBuildings = () => {
		
		if( !isFocused ) {
			return;
		}
		
		// Check if they are empty
		if( !_.isEmpty( _managementBuilding ) ) {
			return;
		}
		
		fetchManagementBuildingTimeslots( kind, subKind, {
			callbackStart: setLoading.bind( this, true ),
			callbackSuccess: setLoading.bind( this, false ),
			callbackError: setLoading.bind( this, false ),
		})
	}
	
	useEffect( () => {
		return fetchBookings();
	}, [ _selectedStartDate ] )
	
	useEffect( () => {
		return fetchBuildings();
	}, [] )
	
	// Collect all employees and add them to the fetch Cache
	useEffect( () => {
		FetchCachedEmployee.fetchEasy( { employee_guids: _.map( employees, ( employee ) => {
			return employee.guid;
		} ) }  );
	}, [ employees ] );
	
	let title = Translate.field( timeslotKind, 'name' )
	if( showEroOnly ) {
		title += ` (${i18n.t('management:menu.ero')})`
	}
	title += `- ${Translate.field( _managementBuilding, 'title' )}`
	
	let subtitle = null;
	if( _managementFloor ) {
		subtitle = Translate.field( _managementFloor, 'title' );
		
		if( _managementRoom ) {
			subtitle = [ subtitle, Translate.field( _managementRoom, 'title' ) ].join(' - ')
		}
	}
	
	const timeslotSize = _.size( timeslots )
	const timeslotMaxRender = 5
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				topNavigation={ true }
				topNavigationProps={{
					showBackButton: true
				}}
				title={ title }
				subtitle={ subtitle }>
				
				<ThemedBlock
					overflow={ true }
					transparent>
					
					<Grid>
						<Row align={"flex-start"} space={ ThemedValues.stackSpace } innerSpan={{ xs: 12, md: 6 }}>
							<Column>
								<ManagementDateSelector />
							</Column>
							<Column>
								<EmployeeSearch
									employees={ employees }
									onChange={ _.debounce( onChangeEmployeeName.bind( this ), 250 ) }
								/>
							</Column>
						</Row>
					</Grid>
				</ThemedBlock>
				
				<Grid>
					<Container>
						<Row align={"flex-start"}>
							{/* Toggles */}
							<Column>
								<ThemedToggle
									onPress={ toggleHideNotScannedIn.bind( this ) }
									checked={ show.current.includes( SHOW.SCANNED_IN ) }
									text={ i18n.t('management:pages.bookings.view.toggle_only_show_not_scanned_in') }
								/>
								
								{ !showEroOnly && (
									<ThemedToggle
										onPress={ toggleShowInvitationsOnly.bind( this ) }
										checked={ show.current.includes( SHOW.INVITATIONS ) }
										text={ i18n.t('management:pages.bookings.view.toggle_only_show_invitations') }
									/>
								)}
							</Column>
							
							{/* Statistic row */}
							<Column>
								<ThemedBlock
									transparent
									style={ staticStyles.themedBlockPaddingHorizontal0PaddingVertical0 }>
									
									<StatisticItem>
										{ i18n.t( 'management:pages.bookings.view.statistic.booking_count', {
											amount: employeeBookingsCount
										} ) }
									</StatisticItem>
									
									<StatisticItem>
										{ i18n.t( 'management:pages.bookings.view.statistic.employee_count', {
											amount: _.size( employees )
										} ) }
									</StatisticItem>
								</ThemedBlock>
							</Column>
						</Row>
					</Container>
				</Grid>
				
				{ loading && (
					<LoadingComponent />
				)}
				
				{ !loading && _.size( timeslots ) === 0 && (
					<Text style={ textStyles.textCenter }>{ i18n.t(`management:generic.no_results` ) }</Text>
				)}
				
				{/* Timeslots */}
				<FlatList
					key={ HelperString.hash('group-list', dateKey ) }
					horizontal={ _.size( timeslots ) > 1 }
					data={ loading ? [] : timeslots }
					keyExtractor={ (item, index) => {
						return HelperString.hash(`group-list-item-${dateKey}`, item.guid);
					} }
					initialNumToRender={ timeslotSize < timeslotMaxRender ? timeslotSize : timeslotMaxRender }
					renderItem={ ({ item, key }) => {
						return (
							<BookingsGroup
								key={ HelperString.hash(`group-list-${dateKey}`, key ) }
								item={ item }
								reload={ fetchBookings.bind( this ) }
								inList={ _.size( timeslots ) > 1 }
							/>
						);
					} }
				/>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

const staticStyles = StyleSheet.create({
	themedBlockPaddingHorizontal0PaddingVertical0: {
		paddingHorizontal: 0,
		paddingVertical: 0,
		marginVertical: 0,
		justifyContent: 'flex-start',
	},
});

export default ManagementBookingsViewScene
